package com.samrudha.portfolio.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.samrudha.portfolio.components.layouts.PageLayoutData
import com.samrudha.portfolio.toSitePalette
import org.jetbrains.compose.web.css.AlignItems

// ─── Styles ───────────────────────────────────────────────────────────────────

val AboutHeroTagStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.9.cssRem)           // ↑ was 0.78
        .letterSpacing(0.18.cssRem)
        .textTransform(TextTransform.Uppercase)
        .opacity(0.6)                   // ↑ was 0.4
        .margin(bottom = 2.cssRem)
}

val AboutHeroH1Style = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(3.5.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.1)
            .margin(bottom = 2.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(5.5.cssRem) }
}

val AboutIntroStyle = CssStyle.base {
    Modifier
        .fontSize(1.08.cssRem)          // ↑ was 1.0
        .lineHeight(1.9)
        .opacity(0.78)                  // ↑ was 0.65
        .maxWidth(60.cssRem)
        .margin(bottom = 1.4.cssRem)
}

val TwoColLabelStyle = CssStyle {
    base {
        Modifier
            .fontFamily("'JetBrains Mono'", "monospace")
            .fontSize(0.85.cssRem)      // ↑ was 0.72
            .letterSpacing(0.18.cssRem)
            .textTransform(TextTransform.Uppercase)
            .opacity(0.45)              // ↑ was 0.32
            .fillMaxWidth()
    }
    Breakpoint.MD { Modifier.width(16.cssRem).minWidth(16.cssRem) }
}

val TwoColContentStyle = CssStyle {
    base { Modifier.fillMaxWidth() }
    Breakpoint.MD { Modifier.flex(1) }
}

val ExpRoleHeadStyle = CssStyle.base {
    Modifier
        .fontFamily("Nunito", "sans-serif")
        .fontSize(1.4.cssRem)           // ↑ was 1.3
        .fontWeight(FontWeight.Normal)
        .margin(bottom = 0.3.cssRem)
}

val ExpMetaStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.85.cssRem)          // ↑ was 0.72
        .letterSpacing(0.04.cssRem)
        .opacity(0.52)                  // ↑ was 0.38
        .margin(bottom = 1.4.cssRem)
}

val ExpBulletStyle2 = CssStyle.base {
    Modifier
        .fontSize(1.0.cssRem)           // ↑ was 0.9
        .lineHeight(1.82)
        .opacity(0.75)                  // ↑ was 0.6
        .display(DisplayStyle.Block)
        .margin(bottom = 0.6.cssRem)
        .padding(left = 1.cssRem)
}

val SkillGroupWrapStyle = CssStyle.base {
    Modifier.margin(bottom = 2.5.cssRem)
}

val SkillGroupHeadStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.82.cssRem)          // ↑ was 0.68
        .letterSpacing(0.14.cssRem)
        .textTransform(TextTransform.Uppercase)
        .opacity(0.45)                  // ↑ was 0.3
        .margin(bottom = 0.9.cssRem)
}

val SkillListStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .flexWrap(FlexWrap.Wrap)
        .gap(0.5.cssRem)
}

val AchievementMarkStyle = CssStyle.base {
    Modifier
        .fontFamily("'JetBrains Mono'", "monospace")
        .fontSize(0.82.cssRem)          // ↑ was 0.68
        .opacity(0.45)                  // ↑ was 0.3
        .minWidth(1.5.cssRem)
}

val AchievementTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.02.cssRem)          // ↑ was 0.95
        .lineHeight(1.7)
        .opacity(0.8)                   // ↑ was 0.7
}

val ContactH2Style = CssStyle {
    base {
        Modifier
            .fontFamily("Nunito", "sans-serif")
            .fontSize(2.4.cssRem)
            .fontWeight(FontWeight.Normal)
            .lineHeight(1.2)
            .margin(bottom = 2.5.cssRem)
    }
    Breakpoint.MD { Modifier.fontSize(3.2.cssRem) }
}

val ContactLinksStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .flexWrap(FlexWrap.Wrap)
        .gap(2.cssRem)
}

// ─── Data ─────────────────────────────────────────────────────────────────────

private data class SkillGroup(val label: String, val items: List<String>)

private val skillGroups = listOf(
    SkillGroup("Languages", listOf("Kotlin", "Java", "C++", "Python", "SQL", "XML")),
    SkillGroup("Android", listOf(
        "Android SDK", "Jetpack Compose", "MVVM / MVI", "ViewModel",
        "Coroutines / Flow", "Room", "Retrofit", "WorkManager", "JNI / NDK"
    )),
    SkillGroup("AI / ML", listOf("On-Device LLM", "Gemma", "Qwen", "MediaPipe", "OpenCV", "ONNX")),
    SkillGroup("Infrastructure", listOf("Firebase", "GCP", "Docker", "Git", "REST APIs", "Stripe")),
)

private val expBullets = listOf(
    "Founded and scaled production apps to 30,000+ organic users without paid acquisition.",
    "Architected on-device LLM inference pipeline using JNI/NDK and quantized ONNX models.",
    "Shipped Bolo App features with Kotlin, MVVM, Jetpack components, and zero-crash release cycles.",
    "Built offline-first sync (Room + Retrofit + WorkManager) for GenzCrop, optimized for low-connectivity rural environments.",
    "Resolved production ANRs and crashes via Android Profiler and Firebase Crashlytics.",
)

private val achievements = listOf(
    "1st Place — Ideathon 1.0 & Ideathon 2.0",
    "1st Place — University Project Competition",
    "30,000+ organic installs across production apps",
    "4.9★ rating across 10+ countries — FocusGuard",
    "2 apps published commercially — Play Store & Amazon Appstore",
)

private data class ContactLink(val label: String, val href: String)

private val contactLinks = listOf(
    ContactLink("samrudhakshirsagar@gmail.com", "mailto:samrudhakshirsagar@gmail.com"),
    ContactLink("+91 9370346710", "tel:+919370346710"),
    ContactLink("LinkedIn ↗", "https://linkedin.com/in/samrudha-kshirsagar"),
    ContactLink("GitHub ↗", "https://github.com/samrudha01codespace"),
)

// ─── Page ──────────────────────────────────────────────────────────────────────

@InitRoute
fun initAboutPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("About"))
}

@Page("/about")
@Layout(".components.layouts.PageLayout")
@Composable
fun AboutPage() {
    val sitePalette = ColorMode.current.toSitePalette()
    val accent = sitePalette.brand.accent
    val colorMode = ColorMode.current

    val border      = if (colorMode.isLight) Color.rgba(0, 0, 0, 35)  else Color.rgba(255, 255, 255, 20)
    val borderFaint = if (colorMode.isLight) Color.rgba(0, 0, 0, 15)  else Color.rgba(255, 255, 255, 10)
    val skillBorder = if (colorMode.isLight) Color.rgba(0, 0, 0, 40)  else Color.rgba(255, 255, 255, 22)

    Column(Modifier.fillMaxWidth()) {

        // ── Hero ──────────────────────────────────────────────────────────────
        Div(attrs = Modifier
            .fillMaxWidth()
            .padding(top = 4.cssRem, bottom = 3.cssRem)
            .borderBottom(1.px, LineStyle.Solid, border)
            .margin(bottom = 0.cssRem)
            .toAttrs()
        ) {
            P(AboutHeroTagStyle.toAttrs()) { Text("About · Samrudha Kshirsagar") }

            Div(AboutHeroH1Style.toAttrs()) {
                SpanText("I build things")
                Br {}
                SpanText("that work")
                Br {}
                SpanText("without the cloud.", Modifier.color(accent))
            }

            P(AboutIntroStyle.toAttrs()) {
                Text(
                    "Android developer and startup co-founder based in Pune, India. " +
                            "I specialize in on-device AI inference, offline-first architecture, " +
                            "and taking products from zero to real users — solo."
                )
            }
            P(AboutIntroStyle.toAttrs()) {
                Text(
                    "For 1.5 years I've been the only engineer at Genzopia Solutions. " +
                            "Every architectural decision, every product call, every production incident — mine. " +
                            "That kind of ownership is what I want to bring to a team."
                )
            }
        }

        // ── Experience ────────────────────────────────────────────────────────
        Div(attrs = Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(2.cssRem)
            .padding(topBottom = 2.5.cssRem)
            .borderTop(1.px, LineStyle.Solid, border)
            .toAttrs()
        ) {
            Span(TwoColLabelStyle.toAttrs()) { Text("// Experience") }
            Div(TwoColContentStyle.toAttrs()) {
                Div(attrs = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 2.cssRem)
                    .borderBottom(1.px, LineStyle.Solid, borderFaint)
                    .toAttrs()
                ) {
                    P(ExpRoleHeadStyle.toModifier().color(accent).toAttrs()) {
                        Text("Android Developer & Co-founder")
                    }
                    P(ExpMetaStyle.toAttrs()) {
                        Text("Genzopia Solutions LLP · Pune, India · Present")
                    }
                    expBullets.forEach { bullet ->
                        Span(ExpBulletStyle2.toAttrs()) { Text("— $bullet") }
                    }
                }
            }
        }

        // ── Stack ─────────────────────────────────────────────────────────────
        Div(attrs = Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(2.cssRem)
            .padding(topBottom = 2.5.cssRem)
            .borderTop(1.px, LineStyle.Solid, border)
            .toAttrs()
        ) {
            Span(TwoColLabelStyle.toAttrs()) { Text("// Stack") }
            Div(TwoColContentStyle.toAttrs()) {
                skillGroups.forEach { group ->
                    Div(SkillGroupWrapStyle.toAttrs()) {
                        P(SkillGroupHeadStyle.toAttrs()) { Text(group.label) }
                        Div(SkillListStyle.toAttrs()) {
                            group.items.forEach { skill ->
                                Span(attrs = Modifier
                                    .fontFamily("'JetBrains Mono'", "monospace")
                                    .fontSize(0.85.cssRem)   // ↑ was 0.72
                                    .letterSpacing(0.03.cssRem)
                                    .padding(leftRight = 0.9.cssRem, topBottom = 0.4.cssRem)
                                    .border(1.px, LineStyle.Solid, skillBorder)
                                    .opacity(0.85)           // ↑ was 0.75
                                    .toAttrs()
                                ) { Text(skill) }
                            }
                        }
                    }
                }
            }
        }

        // ── Achievements ──────────────────────────────────────────────────────
        Div(attrs = Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(2.cssRem)
            .padding(topBottom = 2.5.cssRem)
            .borderTop(1.px, LineStyle.Solid, border)
            .toAttrs()
        ) {
            Span(TwoColLabelStyle.toAttrs()) { Text("// Achievements") }
            Div(TwoColContentStyle.toAttrs()) {
                achievements.forEach { item ->
                    Div(attrs = Modifier
                        .fillMaxWidth()
                        .padding(topBottom = 1.2.cssRem)
                        .borderBottom(1.px, LineStyle.Solid, borderFaint)
                        .display(DisplayStyle.Flex)
                        .gap(1.5.cssRem)
                        .alignItems(AlignItems.Baseline)
                        .toAttrs()
                    ) {
                        Span(AchievementMarkStyle.toModifier().color(accent).toAttrs()) { Text("✦") }
                        Span(AchievementTextStyle.toAttrs()) { Text(item) }
                    }
                }
            }
        }

        // ── Education ─────────────────────────────────────────────────────────
        Div(attrs = Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(2.cssRem)
            .padding(topBottom = 2.5.cssRem)
            .borderTop(1.px, LineStyle.Solid, border)
            .toAttrs()
        ) {
            Span(TwoColLabelStyle.toAttrs()) { Text("// Education") }
            Div(TwoColContentStyle.toAttrs()) {
                Div(attrs = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 2.cssRem)
                    .borderBottom(1.px, LineStyle.Solid, borderFaint)
                    .toAttrs()
                ) {
                    P(ExpRoleHeadStyle.toModifier().color(accent).toAttrs()) {
                        Text("B.E. Electronics & Telecom")
                    }
                    P(ExpMetaStyle.toAttrs()) {
                        Text("Nutan Maharashtra Institute of Engineering & Technology, Pune · Expected May 2027")
                    }
                }
            }
        }

        // ── Contact ───────────────────────────────────────────────────────────
        Div(attrs = Modifier
            .fillMaxWidth()
            .padding(top = 3.cssRem, bottom = 4.cssRem)
            .borderTop(1.px, LineStyle.Solid, border)
            .toAttrs()
        ) {
            Div(ContactH2Style.toAttrs()) {
                SpanText("Let's build something")
                Br {}
                SpanText("that works offline.")
            }
            Div(ContactLinksStyle.toAttrs()) {
                contactLinks.forEach { link ->
                    A(
                        href = link.href,
                        attrs = Modifier
                            .fontFamily("'JetBrains Mono'", "monospace")
                            .fontSize(0.95.cssRem)   // ↑ was 0.82
                            .letterSpacing(0.06.cssRem)
                            .textDecorationLine(TextDecorationLine.None)
                            .color(accent)
                            .opacity(0.72)           // ↑ was 0.6
                            .toAttrs()
                    ) { Text(link.label) }
                }
            }
        }
    }
}